import React, { useState, useEffect } from 'react';
import { Modal, Upload, Button, Row, Col, notification } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { getAllAvatarImages } from '../../api/Avatar';

const AvatarUploadModal = ({ visible, onClose, onAvatarSelect, botName }) => {
    const [avatars, setAvatars] = useState([]);
    const [selectedPresetAvatar, setSelectedPresetAvatar] = useState(null);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);

    useEffect(() => {
        if (visible) {
            fetchAvatars();
        }
    }, [visible]);

    const fetchAvatars = async () => {
        try {
            const response = await getAllAvatarImages();
            setAvatars(response.avatars);
        } catch (error) {
            notification.error({
                message: 'Fetch Failed',
                description: 'Failed to fetch avatar images'
            });
            console.error('Error fetching avatars:', error);
        }
    };

    const handlePresetAvatarSelect = (avatar) => {
        setSelectedPresetAvatar(avatar);
        setUploadedFile(null);
    };

    const validateFile = (file) => {
        const isValidType = ['image/jpeg', 'image/png', 'image/gif', 'image/jpg'].includes(file.type);
        const isValidSize = file.size <= 5 * 1024 * 1024;

        if (!isValidType) {
            notification.error({
                message: 'Invalid file type',
                description: 'Please upload a JPEG, PNG or GIF file'
            });
            return false;
        }

        if (!isValidSize) {
            notification.error({
                message: 'File too large',
                description: 'Image must be smaller than 5MB'
            });
            return false;
        }

        return true;
    };

    const handleFileChange = ({ file }) => {
        if (validateFile(file)) {
            setUploadedFile(file);
            setSelectedPresetAvatar(null);
        }
    };

    const handleSubmit = async () => {
        if (!selectedPresetAvatar && !uploadedFile) {
            notification.warning({
                message: 'No selection',
                description: 'Please select an avatar or upload an image'
            });
            return;
        }

        setIsUploading(true);
        try {
            if (selectedPresetAvatar) {
                await onAvatarSelect({
                    botName,
                    imageUrl: selectedPresetAvatar.imageUrl,
                    file: null
                });
            } else if (uploadedFile) {
                await onAvatarSelect({
                    botName,
                    file: uploadedFile
                });
            }

            onClose();
            setSelectedPresetAvatar(null);
            setUploadedFile(null);
        } catch (error) {
            notification.error({
                message: 'Selection failed',
                description: error.message || 'Failed to update avatar'
            });
        } finally {
            setIsUploading(false);
        }
    };

    const handleClose = () => {
        setSelectedPresetAvatar(null);
        setUploadedFile(null);
        onClose();
    };

    return (
        <Modal
            visible={visible}
            title="Choose Your Avatar Image"
            onCancel={handleClose}
            width={800}
            footer={[
                <Button
                    key="submit"
                    type="primary"
                    className="save-button"
                    onClick={handleSubmit}
                    loading={isUploading}
                >
                    Save Avatar
                </Button>,
                <Button key="back" className="cancel-button" onClick={handleClose}>
                    Cancel
                </Button>
            ]}
            style={{ top: '50px' }}
        >
            <div className="p-4">
                <div className="mb-8">
                    <h3 className="text-lg font-semibold mb-4">Available Avatars Images</h3>
                    <div className="avatar-container" style={{ maxHeight: '410px', overflowY: 'auto', paddingLeft: '10px' }}>
                        <Row gutter={[16, 16]}>
                            {avatars.map((avatar) => (
                                <Col span={8} key={avatar.id}>
                                    <div
                                        onClick={() => handlePresetAvatarSelect(avatar)}
                                        className={`cursor-pointer transition-all duration-300 p-2 rounded ${selectedPresetAvatar?.id === avatar.id
                                            ? 'bg-gray-100 ring-2 ring-gray-500'
                                            : 'hover:bg-gray-50'
                                            }`}
                                        style={{
                                            boxShadow: selectedPresetAvatar?.id === avatar.id ? '0 10px 20px rgba(0, 0, 0, 0.5)' : 'none', // Custom shadow
                                        }}
                                    >
                                        <img
                                            src={avatar.imageUrl}
                                            alt={`Avatar ${avatar.id}`}
                                            className={`object-cover rounded transition-transform duration-300 ${selectedPresetAvatar?.id === avatar.id
                                                ? 'scale-105'
                                                : ''
                                                }`}
                                            style={{
                                                height: '120px',
                                                width: '100%',
                                                objectFit: 'cover',
                                            }}
                                        />
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </div>
                </div>

                <div className="mt-8">
                    <h3 className="text-lg font-semibold mb-4 mt-4">Upload Custom Image</h3>
                    <Upload
                        accept="image/*"
                        beforeUpload={() => false}
                        onChange={handleFileChange}
                        maxCount={1}
                        showUploadList={false}
                    >
                        <Button icon={<PlusOutlined />} className="w-full h-12 upload-button-styling-modal">
                            Upload Image (Optional)
                        </Button>
                    </Upload>

                    {uploadedFile && (
                        <div className="mt-4 text-center">
                            <img
                                src={URL.createObjectURL(uploadedFile)}
                                alt="Preview"
                                className="max-w-xs mx-auto rounded shadow-lg ring-2 ring-gray-300"
                            />
                            <p className="mt-2 text-gray-600">{uploadedFile.name}</p>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export { AvatarUploadModal };